// Meshes for the map: ground, roads, fields, water and fords, capture-point rings, ruins, fences, buildings,
// cliffs, bases, boss pits, ink camps, instanced trees/rocks/decor/props, and grass. The layout DATA comes from core/layout —
// this file keeps no copy of it, only draws it. Everything built lands under one group, so a map editor can
// throw the whole map away and build it again after every change.
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { mk, flat, release, TEAM, type MkOpts } from './engrave.js';
import { LAYOUT, PROP_SHAPES, baseParts, inEllipse, inRect, terrain, type Layout, type Pt, type Team } from '../core/layout.js';
import { inAuthoredHalf } from '../core/map/expand.js';
import { distSeg, distPolyline } from '../core/map/geometry.js';
import type { Building, Cliff, Decor, Patch, Prop, Road, Wall } from '../core/map/schema.js';

/* Deterministic decorative randomness only — never gameplay. */
function rng(seed: number) {
  return () => {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
/* An object's look is seeded by where it stands (to the centimetre), never by its index: adding, moving or
   deleting one object in the editor must not reshuffle how the others look. */
const spot = (x: number, z: number, salt = 0) =>
  rng(Math.imul(Math.round(x * 100), 73856093) ^ Math.imul(Math.round(z * 100), 19349663) ^ salt);

function strip(root: THREE.Object3D, a: Pt, b: Pt, width: number, color: number, y: number) {
  const dx = b[0] - a[0], dz = b[1] - a[1], len = Math.hypot(dx, dz);
  const m = mk(new THREE.PlaneGeometry(len + width * 0.6, width), color, { cast: false });
  m.rotation.set(-Math.PI / 2, 0, Math.atan2(-dz, dx));
  m.position.set((a[0] + b[0]) / 2, y, (a[1] + b[1]) / 2);
  root.add(m);
  return m;
}

/* A box (or any geometry) laid along segment a→b at height y: its local x runs along the segment. */
function along(root: THREE.Object3D, geo: THREE.BufferGeometry, color: number, opts: MkOpts, a: Pt, b: Pt, y: number) {
  const m = mk(geo, color, opts);
  m.rotation.y = Math.atan2(-(b[1] - a[1]), b[0] - a[0]);
  m.position.set((a[0] + b[0]) / 2, y, (a[1] + b[1]) / 2);
  root.add(m);
  return m;
}
const segLen = (a: Pt, b: Pt) => Math.hypot(b[0] - a[0], b[1] - a[1]);
const segs = (pts: Pt[], closed = false) => {
  const out: [Pt, Pt][] = [];
  for (let i = 0; i < pts.length - (closed ? 0 : 1); i++) out.push([pts[i]!, pts[(i + 1) % pts.length]!]);
  return out;
};
/* Evenly spaced stations along a polyline (both ends included), about `step` apart. */
function stations(pts: Pt[], step: number, closed = false): Pt[] {
  const out: Pt[] = [];
  for (const [a, b] of segs(pts, closed)) {
    const n = Math.max(1, Math.round(segLen(a, b) / step));
    for (let k = out.length ? 1 : 0; k <= n; k++) out.push([a[0] + (b[0] - a[0]) * k / n, a[1] + (b[1] - a[1]) * k / n]);
  }
  return out;
}
/* Map polygon → a THREE.Shape whose XY plane, rotated flat (−π/2 about x), lands on the ground. */
const groundShape = (pts: Pt[]) => new THREE.Shape(pts.map(([x, z]) => new THREE.Vector2(x, -z)));

/* Instanced parts collected over the whole map, one InstancedMesh (one draw call) per part at the end. */
function batcher() {
  const parts = new Map<string, { geo: () => THREE.BufferGeometry; color: number; opts: MkOpts; list: THREE.Matrix4[] }>();
  const o = new THREE.Object3D();
  return {
    /** T places the whole object, local positions the part inside it. */
    put(key: string, geo: () => THREE.BufferGeometry, color: number, opts: MkOpts, T: THREE.Matrix4, local?: THREE.Matrix4) {
      let p = parts.get(key);
      if (!p) parts.set(key, p = { geo, color, opts, list: [] });
      p.list.push(local ? T.clone().multiply(local) : T.clone());
    },
    trs(x: number, y: number, z: number, ry = 0, s: number | [number, number, number] = 1, rx = 0, rz = 0) {
      o.position.set(x, y, z); o.rotation.set(rx, ry, rz);
      if (typeof s === 'number') o.scale.setScalar(s); else o.scale.set(...s);
      o.updateMatrix();
      return o.matrix.clone();
    },
    flush(root: THREE.Object3D) {
      for (const p of parts.values()) {
        const mesh = mk(p.geo(), p.color, { ...p.opts, count: p.list.length }) as THREE.InstancedMesh;
        p.list.forEach((m, n) => mesh.setMatrixAt(n, m));
        mesh.computeBoundingSphere();
        root.add(mesh);
      }
    },
  };
}
type Batcher = ReturnType<typeof batcher>;

const STONE = 0xb0aa9c, STONE_LIGHT = 0xbdb6a8, STONE_DARK = 0xa8a296, WOOD = 0x8a6a48, WOOD_DARK = 0x6a4c34;
const LEAF = 0x3d7f46, LEAF_DARK = 0x2f6d3c, HEDGE = 0x2c5c34, ROCK = 0x8f8c84, WATER = 0x5f7682, BANK = 0x8a8a70;

/* Round caps of radius r at every vertex: with a box of width 2r per segment they make the capsule the
   collision uses (layout.ts), so nothing drawn sticks out of what blocks. */
function caps(root: THREE.Object3D, pts: Pt[], r: number, h: number, color: number, opts: MkOpts) {
  for (const [x, z] of pts) {
    const cap = mk(new THREE.CylinderGeometry(r, r, h, 10), color, opts);
    cap.position.set(x, h / 2, z);
    root.add(cap);
  }
}
/* Segment a→b moved sideways by `off` (to its left, looking from a to b). */
function offset(a: Pt, b: Pt, off: number): [Pt, Pt] {
  const len = segLen(a, b), nx = -(b[1] - a[1]) / len * off, nz = (b[0] - a[0]) / len * off;
  return [[a[0] + nx, a[1] + nz], [b[0] + nx, b[1] + nz]];
}

/* Walls block within 0.35 m of their line; every style stays inside that capsule. */
const WALL_R = 0.35;
function buildFence(root: THREE.Object3D, B: Batcher, w: Wall) {
  const D = WALL_R * 2;
  if (w.style === 'stone') {
    for (const [a, b] of segs(w.points)) along(root, new THREE.BoxGeometry(segLen(a, b), 1.3, D), STONE, { team: 'prop' }, a, b, 0.65);
    caps(root, w.points, WALL_R, 1.3, STONE, { team: 'prop' });
    return;
  }
  if (w.style === 'palisade') {
    // stakes on a sill beam as wide as the collision, rails pinned along both faces
    for (const [a, b] of segs(w.points)) {
      along(root, new THREE.BoxGeometry(segLen(a, b), 0.3, D), WOOD_DARK, { team: 'prop' }, a, b, 0.15);
      for (const side of [-1, 1]) for (const y of [0.6, 1.2]) {
        const [p, q] = offset(a, b, side * (WALL_R - 0.05));
        along(root, new THREE.BoxGeometry(segLen(a, b), 0.12, 0.1), WOOD_DARK, { team: 'prop' }, p, q, y);
      }
    }
    caps(root, w.points, WALL_R, 0.3, WOOD_DARK, { team: 'prop' });
    for (const [x, z] of stations(w.points, 0.4)) {
      const r = spot(x, z, 1), h = 1.55 + r() * 0.4, lean = (r() - 0.5) * 0.08;
      B.put('stake', () => new THREE.CylinderGeometry(0.2, 0.22, 1, 6).translate(0, 0.5, 0), WOOD, { team: 'prop' },
        B.trs(x, 0, z, r() * 3, [1, h, 1], lean, lean));
      B.put('stakeTip', () => new THREE.ConeGeometry(0.2, 0.4, 6).translate(0, 0.2, 0), WOOD_DARK, { team: 'prop' },
        B.trs(x, h, z, 0, 1, lean, lean));
    }
    return;
  }
  // hedge: a dark clipped core with leafy lumps along its top
  for (const [a, b] of segs(w.points)) along(root, new THREE.BoxGeometry(segLen(a, b), 1.05, D), HEDGE, { team: 'foliage' }, a, b, 0.525);
  caps(root, w.points, WALL_R, 1.05, HEDGE, { team: 'foliage' });
  for (const [x, z] of stations(w.points, 0.6)) {
    const r = spot(x, z, 2), light = r() < 0.5;
    B.put(light ? 'hedgeLumpLight' : 'hedgeLump', () => new THREE.IcosahedronGeometry(1, 1), light ? 0x356b3c : HEDGE, { team: 'foliage' },
      B.trs(x, 0.85 + r() * 0.2, z, r() * 3, WALL_R + r() * 0.08));
  }
}

function buildBuilding(root: THREE.Object3D, b: Building) {
  const g = new THREE.Group();
  g.position.set(b.x, 0, b.z);
  g.rotation.y = -b.rot;  // map rot turns local x toward +z; three's rotation.y turns it toward −z
  root.add(g);
  const r = spot(b.x, b.z, 3);
  // work in a frame whose x is the long side; turn it back if the building is deeper than wide
  const long = Math.max(b.w, b.d), short = Math.min(b.w, b.d);
  const f = new THREE.Group();
  if (b.d > b.w) f.rotation.y = Math.PI / 2;
  g.add(f);
  const part = (geo: THREE.BufferGeometry, color: number, x: number, y: number, z: number, opts: MkOpts = { team: 'prop' }) => {
    const m = mk(geo, color, opts);
    m.position.set(x, y, z);
    f.add(m);
    return m;
  };

  if (b.style === 'house') {
    const wallH = 2.1, roofH = Math.min(2.4, short * 0.5), over = 0.3;
    part(new THREE.BoxGeometry(long, wallH, short), 0xd9ccaa, 0, wallH / 2, 0);
    // gable roof: a triangular prism along the long side, eaves overhanging the walls
    const tri = new THREE.Shape([new THREE.Vector2(-short / 2 - over, 0), new THREE.Vector2(short / 2 + over, 0), new THREE.Vector2(0, roofH)]);
    const roof = new THREE.ExtrudeGeometry(tri, { depth: long + over * 2, bevelEnabled: false }).translate(0, 0, -(long + over * 2) / 2).rotateY(Math.PI / 2);
    part(roof, 0x9a5c46, 0, wallH, 0);
    part(new THREE.BoxGeometry(0.45, roofH + 0.5, 0.45), 0x8e877a, long * 0.25, wallH + (roofH + 0.5) / 2, -short * 0.12);
    part(new THREE.BoxGeometry(0.8, 1.35, 0.08), 0x5a4030, (r() - 0.5) * long * 0.4, 0.675, short / 2 + 0.04);
    for (const k of [-1, 1]) part(new THREE.BoxGeometry(0.55, 0.5, 0.06), 0x4a3a2c, k * long * 0.3, 1.35, short / 2 + 0.03);
    return g;
  }
  if (b.style === 'mill') {
    // a tapered octagonal stone body, a thatch cone and sails on the long front face — all of it, sails at
    // any angle included, inside the footprint that blocks (a hero must not stand in a blade)
    const rBot = short * 0.48, rTop = short * 0.36, h = 4.2;
    part(new THREE.CylinderGeometry(rTop, rBot, h, 8), 0xd9ccaa, 0, h / 2, 0);
    part(new THREE.ConeGeometry(Math.min(rTop + 0.3, short / 2), 1.7, 8), 0x9a5c46, 0, h + 0.85, 0);
    part(new THREE.BoxGeometry(0.8, 1.4, 0.08), 0x5a4030, 0, 0.7, rBot - (rBot - rTop) * 0.7 / h - 0.03).rotation.x = -Math.atan((rBot - rTop) / h);
    part(new THREE.BoxGeometry(0.45, 0.5, 0.06), 0x4a3a2c, 0, 2.7, (rBot + rTop) / 2 + 0.02);
    const hubZ = Math.min(rTop + 0.35, short / 2 - 0.25), hubY = h - 0.2;
    part(new THREE.CylinderGeometry(0.16, 0.16, 0.5, 8).rotateX(Math.PI / 2), WOOD_DARK, 0, hubY, hubZ - 0.1);
    // a sail tip sweeps a circle of about hypot(len, SAIL_SPAN): keep it within the long half-side
    const len = Math.min(hubY - 1, Math.sqrt(Math.max(0.25, (long / 2 - 0.05) ** 2 - SAIL_SPAN ** 2)));
    const sails = part(millSails(len), WOOD, 0, hubY, hubZ + 0.15);
    sails.rotation.z = r() * Math.PI;
    // sails turn slowly on their own: rotated just before each draw, from the clock, so nothing drives them
    const phase = sails.rotation.z;
    sails.onBeforeRender = () => {
      sails.rotation.z = phase + performance.now() * 0.00035;
      sails.updateMatrixWorld();
    };
    return g;
  }
  if (b.style === 'tower') {
    const h = 4.4;
    part(new THREE.BoxGeometry(long + 0.3, 0.4, short + 0.3), STONE_DARK, 0, 0.2, 0);
    part(new THREE.BoxGeometry(long, h, short), STONE_LIGHT, 0, h / 2, 0);
    part(new THREE.BoxGeometry(long - 0.5, 0.06, short - 0.5), 0x8e877a, 0, h + 0.03, 0);  // the roof walk, a shade darker
    // crenellations along the parapet
    for (const [x, z] of stations([[-long / 2, -short / 2], [long / 2, -short / 2], [long / 2, short / 2], [-long / 2, short / 2]], 0.9, true).slice(0, -1)) {
      part(new THREE.BoxGeometry(0.42, 0.55, 0.42), STONE_LIGHT, x * 0.93, h + 0.27, z * 0.93);
    }
    part(new THREE.BoxGeometry(0.9, 1.6, 0.08), 0x4a3a2c, 0, 0.8, short / 2 + 0.04);
    for (const y of [2.3, 3.4]) part(new THREE.BoxGeometry(0.16, 0.6, 0.06), 0x3a2e24, 0, y, short / 2 + 0.03);
    return g;
  }
  // ruined chapel: floor slab, broken side walls, a standing east end, a rubble-choked doorway, rubble
  // inside. The whole rectangle blocks, so the walls stay closed: a breach is a low stub heaped with rubble.
  const t = 0.45;
  const rubble = (x: number, y: number, z: number) => {
    const rock = part(new THREE.DodecahedronGeometry(0.22 + r() * 0.08), ROCK, x, y, z);
    rock.rotation.set(r() * 3, r() * 3, 0);
  };
  part(new THREE.BoxGeometry(long, 0.15, short), STONE_DARK, 0, 0.075, 0);
  for (const side of [-1, 1]) {
    const pieces = 4, breach = Math.floor(r() * pieces), len = long / pieces, z = side * (short / 2 - t / 2);
    for (let k = 0; k < pieces; k++) {
      const x = -long / 2 + len * (k + 0.5), h = k === breach ? 0.55 : 0.9 + r() * 2;
      part(new THREE.BoxGeometry(len, h, t), STONE_LIGHT, x, h / 2, z);
      if (k === breach) for (const dx of [-0.25, 0.25]) rubble(x + dx * len, h + 0.1, z);
    }
  }
  part(new THREE.BoxGeometry(t, 3.3, short), STONE_LIGHT, -long / 2 + t / 2, 1.65, 0);
  const gable = new THREE.Shape([new THREE.Vector2(-short / 2, 0), new THREE.Vector2(short / 2, 0), new THREE.Vector2(0, short * 0.35)]);
  part(new THREE.ExtrudeGeometry(gable, { depth: t, bevelEnabled: false }).translate(0, 0, -t / 2).rotateY(Math.PI / 2), STONE_LIGHT, -long / 2 + t / 2, 3.3, 0);
  const door = 1.5, stub = (short - door) / 2, fx = long / 2 - t / 2;
  part(new THREE.BoxGeometry(t, 2.4, stub), STONE_LIGHT, fx, 1.2, -(door + stub) / 2);
  part(new THREE.BoxGeometry(t, 1.5, stub), STONE_LIGHT, fx, 0.75, (door + stub) / 2);
  part(new THREE.BoxGeometry(t, 0.5, door), STONE_DARK, fx, 0.25, 0);
  for (const dz of [-0.45, 0, 0.45]) rubble(fx, 0.6, dz);
  part(new THREE.BoxGeometry(0.9, 0.9, 1.3), STONE_DARK, -long / 2 + 1.2, 0.6, 0);  // altar
  for (let k = 0; k < 4; k++) rubble((r() - 0.3) * long * 0.6, 0.3, (r() - 0.5) * short * 0.6);
  return g;
}

/* Four lattice sails as one geometry in the XY plane (hub at the origin), so one mesh turns them all. Each
   sail reaches SAIL_SPAN to the side of its spar. */
const SAIL_SPAN = 0.75;
function millSails(len: number) {
  const parts: THREE.BufferGeometry[] = [];
  for (let k = 0; k < 4; k++) {
    const a = k * Math.PI / 2, rot = new THREE.Matrix4().makeRotationZ(a);
    parts.push(new THREE.BoxGeometry(0.14, len, 0.1).translate(0, len / 2, 0).applyMatrix4(rot));
    parts.push(new THREE.BoxGeometry(0.62, len * 0.72, 0.04).translate(0.38, len * 0.6, 0).applyMatrix4(rot));
    for (let i = 1; i <= 3; i++) parts.push(new THREE.BoxGeometry(0.72, 0.05, 0.06).translate(0.36, len * (0.24 + i * 0.18), 0.04).applyMatrix4(rot));
  }
  const geo = mergeGeometries(parts);
  for (const p of parts) p.dispose();
  return geo;
}

function inPolygon(x: number, z: number, pts: Pt[]) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, zi] = pts[i]!, [xj, zj] = pts[j]!;
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}
const distRing = (x: number, z: number, pts: Pt[]) => Math.min(distPolyline(x, z, pts), distSeg(x, z, pts[pts.length - 1]!, pts[0]!));

/* An open cliff blocks within 0.5 m of its line (layout.ts CLIFF_HALF); a closed one, its polygon. */
const CLIFF_HALF = 0.5;
function buildCliff(root: THREE.Object3D, B: Batcher, c: Cliff) {
  const H = c.closed ? 1.3 : 0.6, CLIFF = 0x948b7a;
  const boulder = (x: number, y: number, z: number, s: number, r: () => number) =>
    B.put('cliffBoulder', () => new THREE.DodecahedronGeometry(1), ROCK, { team: 'prop' }, B.trs(x, y, z, r() * 3, s, r() * 3));
  if (c.closed) {
    // a raised rock shelf with sheer sides: impassable ground reads as a block of land you can't climb
    const shelf = mk(new THREE.ExtrudeGeometry(groundShape(c.points), { depth: H, bevelEnabled: false }), CLIFF, { team: 'prop' });
    shelf.rotation.x = -Math.PI / 2;
    root.add(shelf);
    // boulders along the rim, pushed inward by their own size so none overhangs the edge
    let area2 = 0;
    for (const [a, b] of segs(c.points, true)) area2 += a[0] * b[1] - b[0] * a[1];
    for (const [a, b] of segs(c.points, true)) {
      const len = segLen(a, b), n = Math.max(1, Math.round(len / 1.3)), inX = -(b[1] - a[1]) / len * Math.sign(area2), inZ = (b[0] - a[0]) / len * Math.sign(area2);
      for (let k = 0; k < n; k++) {
        const px = a[0] + (b[0] - a[0]) * k / n, pz = a[1] + (b[1] - a[1]) * k / n, r = spot(px, pz, 4);
        let s = 0.45 + r() * 0.35;
        const x = px + inX * s, z = pz + inZ * s;
        if (!inPolygon(x, z, c.points)) continue;
        s = Math.min(s, distRing(x, z, c.points));
        if (s > 0.2) boulder(x, H, z, s, r);
      }
    }
    return;
  }
  // a ledge line: a low rock band as thick as the strip that blocks movement, heaped with boulders inside it
  for (const [a, b] of segs(c.points)) along(root, new THREE.BoxGeometry(segLen(a, b), H, CLIFF_HALF * 2), CLIFF, { team: 'prop' }, a, b, H / 2);
  caps(root, c.points, CLIFF_HALF, H, CLIFF, { team: 'prop' });
  for (const [a, b] of segs(c.points)) {
    const len = segLen(a, b), n = Math.max(1, Math.round(len / 0.8)), nx = -(b[1] - a[1]) / len, nz = (b[0] - a[0]) / len;
    for (let k = 0; k <= n; k++) {
      const px = a[0] + (b[0] - a[0]) * k / n, pz = a[1] + (b[1] - a[1]) * k / n, r = spot(px, pz, 4);
      const s = 0.3 + r() * 0.18, side = (r() - 0.5) * 2 * (CLIFF_HALF - s);
      boulder(px + nx * side, 0.55 + r() * 0.2, pz + nz * side, s, r);
    }
  }
}

function buildPond(root: THREE.Object3D, pts: Pt[]) {
  // bank: strips along the edge, half of each covered by the water on top — same layers as a stream
  for (const [a, b] of segs(pts, true)) strip(root, a, b, 1, BANK, 0.12);
  root.add(flat(mk(new THREE.ShapeGeometry(groundShape(pts)), WATER, { cast: false }), 0.16));
}

/* Decor parts in the object's own frame (unit scale, facing its local +z). */
function buildDecor(B: Batcher, d: Decor) {
  const r = spot(d.x, d.z, 5), T = B.trs(d.x, 0, d.z, -d.rot, d.scale);
  const at = (x: number, y: number, z: number, s: number | [number, number, number] = 1, ry = 0) => B.trs(x, y, z, ry, s);
  switch (d.kind) {
    case 'bush':
      for (const [x, y, z, s] of [[0, 0.38, 0, 0.5], [0.36, 0.28, 0.14, 0.36], [-0.3, 0.26, -0.18, 0.34]] as const) {
        const dark = r() < 0.35;
        B.put(dark ? 'bushDark' : 'bush', () => new THREE.IcosahedronGeometry(1, 1), dark ? LEAF_DARK : LEAF, { team: 'foliage' }, T, at(x, y, z, s, r() * 3));
      }
      return;
    case 'grave':
      B.put('graveSlab', () => new THREE.BoxGeometry(0.62, 0.8, 0.16).translate(0, 0.4, 0), STONE_LIGHT, { team: 'prop' }, T);
      B.put('graveTop', () => new THREE.CylinderGeometry(0.31, 0.31, 0.16, 12).rotateX(Math.PI / 2), STONE_LIGHT, { team: 'prop' }, T, at(0, 0.8, 0));
      B.put('graveMound', () => new THREE.SphereGeometry(1, 10, 6), 0x8a7a5a, { team: 'prop', cast: false }, T, at(0, 0, 0.62, [0.42, 0.14, 0.62]));
      return;
    case 'post':
      B.put('post', () => new THREE.CylinderGeometry(0.09, 0.11, 1.5, 6).translate(0, 0.75, 0), WOOD_DARK, { team: 'prop' }, T);
      B.put('postBoard', () => new THREE.BoxGeometry(0.72, 0.26, 0.05), WOOD, { team: 'prop' }, T, at(0.18, 1.22, 0.1));
      return;
    case 'barrel':
      B.put('barrel', () => new THREE.CylinderGeometry(0.3, 0.3, 0.84, 10).translate(0, 0.42, 0), WOOD, { team: 'prop' }, T);
      for (const y of [0.18, 0.66]) B.put('barrelHoop', () => new THREE.CylinderGeometry(0.325, 0.325, 0.07, 10, 1, true), 0x4a3a2c, { team: 'prop' }, T, at(0, y, 0));
      return;
  }
}

/* Props in their own frame (facing local +z, local x along a cart's length). Every part stays inside the
   footprint layout.ts's PROP_SHAPES blocks: nothing drawn sticks out of what stops a hero. */
const HAY = 0xc9a74e, HAY_DARK = 0xa88a3a;
function buildProp(B: Batcher, p: Prop) {
  const r = spot(p.x, p.z, 9), T = B.trs(p.x, 0, p.z, -p.rot);
  const at = (x: number, y: number, z: number, s: number | [number, number, number] = 1, ry = 0, rx = 0, rz = 0) => B.trs(x, y, z, ry, s, rx, rz);
  const R = PROP_SHAPES[p.kind].r ?? 0;
  switch (p.kind) {
    case 'haystack': {
      const h = 1.4 + r() * 0.3, rr = R * 0.95;
      B.put(r() < 0.5 ? 'hay' : 'hayDark', () => new THREE.SphereGeometry(1, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), r() < 0.5 ? HAY : HAY_DARK, { team: 'prop' }, T, at(0, 0, 0, [rr, h, rr], r() * 3));
      B.put('hayBand', () => new THREE.CylinderGeometry(1, 1, 0.08, 14, 1, true), WOOD_DARK, { team: 'prop' }, T, at(0, h * 0.4, 0, [rr * 0.93, 1, rr * 0.93]));
      B.put('hayPole', () => new THREE.CylinderGeometry(0.05, 0.06, 0.7, 5).translate(0, 0.35, 0), WOOD_DARK, { team: 'prop' }, T, at(0, h - 0.1, 0));
      return;
    }
    case 'cart': {
      B.put('cartBed', () => new THREE.BoxGeometry(2, 0.12, 1.1), WOOD, { team: 'prop' }, T, at(-0.2, 0.62, 0));
      for (const z of [-0.52, 0.52]) B.put('cartSide', () => new THREE.BoxGeometry(2, 0.3, 0.06), WOOD_DARK, { team: 'prop' }, T, at(-0.2, 0.83, z));
      for (const z of [-0.61, 0.61]) B.put('cartWheel', () => new THREE.CylinderGeometry(0.42, 0.42, 0.1, 12).rotateX(Math.PI / 2), WOOD_DARK, { team: 'prop' }, T, at(-0.3, 0.42, z, 1, 0, 0, r() * 3));
      for (const z of [-0.3, 0.3]) B.put('cartShaft', () => new THREE.BoxGeometry(0.5, 0.08, 0.08), WOOD_DARK, { team: 'prop' }, T, at(1.04, 0.52, z));
      for (const [x, z] of [[-0.75, -0.2], [-0.15, 0.18]] as const) {
        B.put('barrel', () => new THREE.CylinderGeometry(0.3, 0.3, 0.84, 10).translate(0, 0.42, 0), WOOD, { team: 'prop' }, T, at(x, 0.68, z, 0.8, r() * 3));
      }
      B.put('barrel', () => new THREE.CylinderGeometry(0.3, 0.3, 0.84, 10).translate(0, 0.42, 0), WOOD, { team: 'prop' }, T, at(0.35, 0.93, -0.3, 0.75, 0, Math.PI / 2));
      return;
    }
    case 'cross':
      B.put('crossBase', () => new THREE.BoxGeometry(0.6, 0.3, 0.6).translate(0, 0.15, 0), STONE_DARK, { team: 'prop' }, T);
      B.put('crossShaft', () => new THREE.BoxGeometry(0.22, 1.7, 0.18).translate(0, 0.85, 0), STONE_LIGHT, { team: 'prop' }, T, at(0, 0.3, 0, 1, 0, 0, (r() - 0.5) * 0.08));
      B.put('crossArm', () => new THREE.BoxGeometry(0.8, 0.2, 0.18), STONE_LIGHT, { team: 'prop' }, T, at(0, 1.55, 0));
      return;
    case 'well':
      B.put('wellWall', () => new THREE.CylinderGeometry(0.95, 1, 0.7, 14).translate(0, 0.35, 0), STONE, { team: 'prop' }, T);
      B.put('wellRim', () => new THREE.TorusGeometry(0.86, 0.1, 6, 16).rotateX(Math.PI / 2), STONE_DARK, { team: 'prop' }, T, at(0, 0.72, 0));
      B.put('wellWater', () => new THREE.CircleGeometry(0.76, 16).rotateX(-Math.PI / 2), 0x3f525c, { team: 'prop', cast: false }, T, at(0, 0.71, 0));
      for (const x of [-0.8, 0.8]) B.put('wellPost', () => new THREE.BoxGeometry(0.12, 1.9, 0.12).translate(0, 0.95, 0), WOOD_DARK, { team: 'prop' }, T, at(x, 0, 0));
      B.put('wellBeam', () => new THREE.CylinderGeometry(0.06, 0.06, 1.6, 6).rotateZ(Math.PI / 2), WOOD, { team: 'prop' }, T, at(0, 1.72, 0));
      B.put('wellRoof', () => new THREE.ConeGeometry(0.95, 0.6, 4).rotateY(Math.PI / 4), 0x9a5c46, { team: 'prop' }, T, at(0, 2.2, 0));
      B.put('wellBucket', () => new THREE.CylinderGeometry(0.14, 0.11, 0.22, 8), WOOD, { team: 'prop' }, T, at(0, 1.25, 0));
      return;
  }
}

/* A group on the ground turned like a building (map rot turns local x toward +z). */
function patchGroup(root: THREE.Object3D, f: Patch) {
  const g = new THREE.Group();
  g.position.set(f.x, 0, f.z);
  g.rotation.y = -f.rot;
  root.add(g);
  return g;
}

/* Roads, visual only, by kind: a tract is set stone between soft kerbs; a dirt road a packed strip with a
   ragged verge, a dark sand stipple, sparse pebbles and a rare grass tuft (a road without a kind is dirt); a
   trail a broken run of trodden patches.
   Layers bottom up: dirt verge 0.079, dirt strip 0.08, tract strip 0.081, trail patches 0.082, sand specks
   0.0835, setts and kerbs 0.084–0.086: each colour on its own height, so overlapping marks (crossing roads
   included) never share a plane. Kerbs run as far past each vertex as the strip does (0.3·w), so bends have
   no gaps.
   The post pass outlines albedo steps from ~0.06 luminance (linear), fully from ~0.16: a detail's colour step
   sets how hard its line is drawn. Setts and kerbs stay 0.07–0.14 off the stone, so they read as soft, partly
   drawn outlines; the sand specks go darker, to read as filled dots. */
const ROAD_DIRT = 0xcdb582, ROAD_STONE = 0xc3baa3, ROAD_SETT = 0xb2aa95, ROAD_SETT_DARK = 0xaba38f, ROAD_KERB = 0xaea692, ROAD_TRAIL = 0xaea26f;
const DIRT_SPECK = 0xa18e65, PEBBLE = 0xb4aa94, PEBBLE_DARK = 0xa0967f, ROAD_GRASS = 0x9cc06f;
const flatBox = () => new THREE.BoxGeometry(1, 0.004, 1);
const disc = () => new THREE.CircleGeometry(1, 10).rotateX(-Math.PI / 2);
const pebbleGeo = () => new THREE.DodecahedronGeometry(1);
const roadBlade = () => new THREE.ConeGeometry(0.022, 0.5, 3, 1, true).translate(0, 0.25, 0);

function pebble(B: Batcher, x: number, z: number, r: () => number) {
  const s = 0.05 + r() * 0.08, dark = r() < 0.4;
  B.put(dark ? 'roadPebbleDark' : 'roadPebble', pebbleGeo, dark ? PEBBLE_DARK : PEBBLE, { cast: false },
    B.trs(x, 0.08, z, r() * 6, [s, s * 0.45, s * (0.7 + r() * 0.3)]));
}
/* Three pen strokes splayed from one spot, like the open-ground grass. */
function tuft(B: Batcher, x: number, z: number, r: () => number) {
  const spin = r() * Math.PI * 2;
  for (let k = 0; k < 3; k++) {
    const ang = spin + k * 2.1;
    B.put('roadGrass', roadBlade, ROAD_GRASS, { cast: false },
      B.trs(x + Math.cos(ang) * 0.06, 0.08, z + Math.sin(ang) * 0.06, r() * 3, [1, 0.5 * (0.7 + r() * 0.5), 1], Math.sin(ang) * 0.45, -Math.cos(ang) * 0.45));
  }
}

function dirtMarks(B: Batcher, len: number, w: number, ang: number, at: (t: number, side: number) => [number, number], r: () => number) {
  // a ragged verge: blobs of the strip's own colour just under it, so its outline wanders instead of running
  // as two straight rails
  for (const side of [-1, 1]) for (let t = r() * 0.5; t < len; t += 0.5 + r() * 0.5) {
    const [x, z] = at(t, side * (w / 2 - 0.12 + r() * 0.14));
    B.put('dirtVerge', disc, ROAD_DIRT, { cast: false }, B.trs(x, 0.079, z, ang + (r() - 0.5) * 0.4, [0.35 + r() * 0.4, 1, 0.16 + r() * 0.16]));
  }
  for (let k = 0; k < len * w * 3; k++) {
    const [x, z] = at(r() * len, (r() - 0.5) * 0.92 * w), s = 0.02 + r() * 0.02;
    B.put('dirtSpeck', disc, DIRT_SPECK, { cast: false }, B.trs(x, 0.0835, z, 0, [s, 1, s]));
  }
  for (let k = 0; k < len * w * 0.4; k++) pebble(B, ...at(r() * len, (r() - 0.5) * 0.9 * w), r);
  for (let t = r() * 4; t < len; t += 3 + r() * 4) {
    const side = r() < 0.5 ? -1 : 1;
    tuft(B, ...at(t, side * (w / 2 - 0.05 + (r() - 0.5) * 0.3)), r);
  }
}

function buildRoad(root: THREE.Object3D, B: Batcher, road: Road) {
  const kind = road.kind ?? 'dirt', w = road.width;
  for (const [a, b] of segs(road.points)) {
    const len = segLen(a, b), ux = (b[0] - a[0]) / len, uz = (b[1] - a[1]) / len, ang = Math.atan2(-uz, ux);
    // a point t metres along the segment, `side` metres to its left
    const at = (t: number, side: number): [number, number] => [a[0] + ux * t - uz * side, a[1] + uz * t + ux * side];
    if (kind === 'trail') {
      const r = spot(a[0] + b[0], a[1] + b[1], 17);
      for (let t = 0.3; t < len; t += 0.75 + r() * 0.5) {
        if (r() < 0.22) continue;  // bare only in places
        const [x, z] = at(t, (r() - 0.5) * 0.3 * w);
        B.put('trailPatch', () => new THREE.CircleGeometry(1, 12).rotateX(-Math.PI / 2), ROAD_TRAIL, { cast: false },
          B.trs(x, 0.082, z, ang + (r() - 0.5) * 0.3, [0.45 + r() * 0.35, 1, w * (0.28 + r() * 0.14)]));
      }
      continue;
    }
    strip(root, a, b, w, kind === 'tract' ? ROAD_STONE : ROAD_DIRT, kind === 'tract' ? 0.081 : 0.08);
    if (kind === 'dirt') {
      dirtMarks(B, len, w, ang, at, spot(a[0] + b[0], a[1] + b[1], 29));
      continue;
    }
    // tract: staggered rows of setts, drawn faintly and thinning out toward the kerbs; most only a pale
    // outline, a few a shade darker; the kerbs are soft lines with their stones ticked off here and there
    const r = spot(a[0] + b[0], a[1] + b[1], 23), inner = w - 0.5, n = Math.max(2, Math.round(inner / 0.5)), sw = inner / n;
    for (let t = 0.2, row = 0; t < len - 0.1; t += 0.42, row++) {
      for (let k = 0; k < n + (row & 1); k++) {
        const side = -inner / 2 + (k + 0.5 - (row & 1) * 0.5) * sw, out = Math.abs(side) / (inner / 2);
        if (out > 1 - 0.05 / (inner / 2) || r() < 0.15 + 0.6 * out * out) continue;
        const [x, z] = at(t, side), dark = r() < 0.12;
        B.put(dark ? 'roadSettDark' : 'roadSett', flatBox, dark ? ROAD_SETT_DARK : ROAD_SETT, { cast: false },
          B.trs(x, dark ? 0.085 : 0.084, z, ang + (r() - 0.5) * 0.12, [0.3, 1, sw - 0.1]));
      }
    }
    for (const side of [-1, 1]) {
      const [x, z] = at(len / 2, side * (w / 2 - 0.06));
      B.put('roadKerb', flatBox, ROAD_KERB, { cast: false }, B.trs(x, 0.086, z, ang, [len + 0.6 * w, 1, 0.05]));
      for (let t = 0.2; t < len; t += 0.55) {
        if (r() < 0.4) continue;
        const [hx, hz] = at(t, side * (w / 2 - 0.14));
        B.put('roadHatch', flatBox, ROAD_KERB, { cast: false }, B.trs(hx, 0.086, hz, ang, [0.04, 1, 0.14]));
      }
    }
  }
}

/* Tilled soil with crop rows along the field's local x: wheat or greens, picked by where the field lies. */
function buildField(root: THREE.Object3D, B: Batcher, f: Patch) {
  const r = spot(f.x, f.z, 10), wheat = r() < 0.5;
  patchGroup(root, f).add(flat(mk(new THREE.PlaneGeometry(f.w, f.d), 0x9a7a4e, { cast: false }), 0.072));
  const T = B.trs(f.x, 0, f.z, -f.rot), rows = Math.max(1, Math.floor(f.d / 0.7)), len = Math.max(0.2, f.w - 0.3);
  for (let k = 0; k < rows; k++) {
    const z = -f.d / 2 + (k + 0.5) * f.d / rows;
    B.put('fieldRidge', () => new THREE.BoxGeometry(1, 0.1, 0.32), 0x86683f, { team: 'prop', cast: false }, T, B.trs(0, 0.09, z, 0, [len, 1, 1]));
    const n = Math.max(1, Math.floor(len / 0.45));
    for (let i = 0; i < n; i++) {
      const x = -len / 2 + (i + 0.5) * len / n;
      B.put(wheat ? 'cropWheat' : 'cropGreen', () => new THREE.ConeGeometry(0.13, 1, 5).translate(0, 0.5, 0), wheat ? 0xd1b35a : 0x6f9a3e, { team: 'foliage', cast: false },
        T, B.trs(x + (r() - 0.5) * 0.12, 0.13, z, r() * 3, [1, 0.3 + r() * 0.25, 1]));
    }
  }
}

/* A shallow crossing: pale rippled water over the rectangle, stepping stones across the stream. */
function buildFord(root: THREE.Object3D, B: Batcher, f: Patch, L: Layout) {
  const r = spot(f.x, f.z, 11);
  patchGroup(root, f).add(flat(mk(new THREE.PlaneGeometry(f.w, f.d), 0x6c848e, { cast: false }), 0.175));
  let dir: Pt = [1, 0], best = Infinity;
  for (const s of L.streams) for (const [a, b] of segs(s)) {
    const d = distSeg(f.x, f.z, a, b);
    if (d < best) { best = d; dir = [b[0] - a[0], b[1] - a[1]]; }
  }
  const len = Math.hypot(dir[0], dir[1]), ux = dir[0] / len, uz = dir[1] / len, reach = Math.hypot(f.w, f.d) / 2;
  // stones step across (along the stream's normal), kept inside the ford; ripples run with the current
  for (let t = -reach; t <= reach; t += 0.75) {
    const side = (r() - 0.5) * 0.5, x = f.x - uz * t + ux * side, z = f.z + ux * t + uz * side;
    if (!inRect(x, z, f, -0.35)) continue;
    B.put('fordStone', () => new THREE.DodecahedronGeometry(1), STONE, { team: 'prop' }, B.trs(x, 0.17, z, r() * 3, [0.32, 0.1, 0.26]));
  }
  const ang = Math.atan2(-uz, ux);
  for (let k = 0; k < Math.ceil(f.w * f.d / 1.5); k++) {
    const x = f.x + (r() - 0.5) * f.w, z = f.z + (r() - 0.5) * f.d;
    if (!inRect(x, z, f, -0.4)) continue;
    B.put('fordRipple', () => new THREE.BoxGeometry(1, 0.01, 0.05), 0xc4d4d8, { team: 'prop', cast: false }, B.trs(x, 0.185, z, ang, [0.4 + r() * 0.6, 1, 1]));
  }
}

/** Which tree grows where: firs on the red half (the authored side of the base diagonal), oaks on the blue. */
export const treeKind = (L: Layout, p: Pt): 'fir' | 'oak' => inAuthoredHalf({ base: L.bases.a }, p) ? 'fir' : 'oak';

/* Flat annulus arcs (0…100% in 5% steps): capture progress lies on the ground, nothing sticks up. */
function arcGeometries(radius: number) {
  return Array.from({ length: 21 }, (_, k) => new THREE.RingGeometry(radius - 0.45, radius, 64, 1, Math.PI / 2, Math.max(0.001, (k / 20) * Math.PI * 2)));
}

export type PointView = { id: string; arcA: THREE.Mesh; arcB: THREE.Mesh; arcs: Record<Team, THREE.RingGeometry[]> };
export type StallView = { hit: THREE.Object3D; hoverRing: THREE.Object3D };
export type MapView = {
  points: PointView[];
  stalls: Record<Team, StallView>;
  /** Removes everything this build added to the scene and frees its geometries and per-mesh materials. */
  dispose(): void;
};

/** Builds the whole static map of `L` under one group added to `scene`. Render only: nav and sight come
    from core/layout. Call dispose() before building another map into the same scene. */
export function buildMap(scene: THREE.Object3D, L: Layout = LAYOUT): MapView {
  const root = new THREE.Group(), B = batcher();
  root.name = 'map';
  scene.add(root);

  root.add(flat(mk(new THREE.PlaneGeometry(420, 320), 0x7fa35a, { cast: false }), 0));
  // ground patches depend on the map's size only, never on its objects
  const rand = rng(21);
  let patchN = 0;
  for (let i = 0; i < 60; i++) {
    const r = 1.8 + rand() * 4, x = (rand() - 0.5) * 2 * L.bounds.x, z = (rand() - 0.5) * 2 * L.bounds.z, c = rand() > 0.5 ? 0x6c934b : 0x8cb266;
    for (const [px, pz] of [[x, z], [-x, -z]] as Pt[]) {
      // each patch its own height (below the roads): overlapping patches must never share a plane
      const p = flat(mk(new THREE.CircleGeometry(r, 24), c, { cast: false }), 0.02 + (patchN++) * 0.0004);
      p.position.x = px; p.position.z = pz;
      root.add(p);
    }
  }

  for (const road of L.roadPaths) buildRoad(root, B, road);
  for (const f of L.fields) buildField(root, B, f);

  // streams, ponds and bridges
  L.streams.forEach((s, k) => {
    const width = L.streamWidths[k] as number;
    for (const [a, b] of segs(s)) {
      strip(root, a, b, width + 0.5, BANK, 0.12);  // muddy bank
      strip(root, a, b, width, WATER, 0.16);
    }
  });
  for (const p of L.ponds) buildPond(root, p);
  for (const f of L.fords) buildFord(root, B, f, L);
  for (const [bx, bz] of L.bridges) {
    // the map validator keeps every bridge on a stream; a deck over nothing still draws, just unaligned
    const k = L.streams.findIndex(st => distPolyline(bx, bz, st) < 1), s = L.streams[k] ?? [];
    const span = Math.max(5.4, (L.streamWidths[k] ?? 0) + 2.4);  // deck runs bank to bank
    let dir: Pt = [1, 0];
    for (const [a, b] of segs(s)) if (distSeg(bx, bz, a, b) < 1) dir = [b[0] - a[0], b[1] - a[1]];
    const ang = Math.atan2(-dir[1], dir[0]);
    const deck = mk(new THREE.BoxGeometry(3.6, 0.22, span), WOOD, { team: 'prop' });
    deck.rotation.y = ang;
    deck.position.set(bx, 0.16, bz);
    root.add(deck);
    for (const sx of [-1.7, 1.7]) for (const sz of [-span / 2 + 0.3, span / 2 - 0.3]) {
      const post = mk(new THREE.CylinderGeometry(0.1, 0.1, 0.8, 6), WOOD_DARK, { team: 'prop' });
      const lx = sx * Math.cos(ang) + sz * Math.sin(ang), lz = -sx * Math.sin(ang) + sz * Math.cos(ang);
      post.position.set(bx + lx, 0.5, bz + lz);
      root.add(post);
    }
  }

  // capture points: chapel floor, neutral ring, per-team capture arcs
  const arcs = { a: arcGeometries(L.pointRadius), b: arcGeometries(L.pointRadius) };
  const points = L.points.map(({ id, pos: [x, z] }): PointView => {
    const floor = flat(mk(new THREE.CircleGeometry(L.pointRadius, 40), 0xdcc98f, { cast: false }), 0.12);
    floor.position.set(x, 0.12, z);
    const ring = flat(mk(new THREE.RingGeometry(L.pointRadius - 0.12, L.pointRadius + 0.08, 64), 0xf2d46b, { cast: false }), 0.16);
    ring.position.set(x, 0.16, z);
    const arcA = flat(mk(arcs.a[0] as THREE.RingGeometry, TEAM.a, { team: 'a', cast: false }), 0.2);
    const arcB = flat(mk(arcs.b[0] as THREE.RingGeometry, TEAM.b, { team: 'b', cast: false }), 0.2);
    arcA.position.set(x, 0.2, z); arcB.position.set(x, 0.2, z);
    root.add(floor, ring, arcA, arcB);
    return { id, arcA, arcB, arcs };
  });

  // ruins: pillars (some broken), walls and fences, buildings, cliffs
  for (const [x, z] of L.pillars) {
    const r = spot(x, z, 6), h = r() < 1 / 3 ? 1.4 : 2.6 + (r() < 0.5 ? 0.6 : 0);
    const p = mk(new THREE.CylinderGeometry(0.5, 0.58, h, 10), STONE_LIGHT, { team: 'prop' });
    p.position.set(x, h / 2, z);
    const cap = mk(new THREE.BoxGeometry(1.3, 0.3, 1.3), STONE_DARK, { team: 'prop' });
    cap.position.set(x, h + 0.15, z);
    root.add(p, cap);
  }
  for (const w of L.fences) buildFence(root, B, w);
  for (const b of L.buildings) buildBuilding(root, b);
  for (const c of L.cliffs) buildCliff(root, B, c);
  for (const d of L.decor) buildDecor(B, d);
  for (const p of L.props) buildProp(B, p);

  // bases: a round floor with the fountain (heals), the team flag and the shop stall
  const stalls = {} as Record<Team, StallView>;
  for (const [team, [x, z]] of Object.entries(L.bases) as [Team, Pt][]) {
    const plat = flat(mk(new THREE.CircleGeometry(4.2, 8), 0xb3ad9f, { cast: false }), 0.14);
    plat.position.set(x, 0.14, z);
    root.add(plat);
    const basin = flat(mk(new THREE.TorusGeometry(1.2, 0.3, 8, 24), 0x9d988e, { team: 'prop' }), 0.3);
    basin.position.set(x, 0.3, z);
    const water = flat(mk(new THREE.CircleGeometry(1.1, 24), 0x9fd0e0, { fx: true, cast: false }), 0.28);
    water.position.set(x, 0.28, z);
    const spout = mk(new THREE.CylinderGeometry(0.18, 0.28, 1.4, 8), STONE_LIGHT, { team: 'prop' });
    spout.position.set(x, 0.7, z);
    const bp = baseParts(team, L);
    const pole = mk(new THREE.CylinderGeometry(0.06, 0.06, 3.6, 6), WOOD_DARK, { team: 'prop' });
    pole.position.set(bp.flag[0], 1.8, bp.flag[1]);
    const flag = mk(new THREE.BoxGeometry(1.2, 0.8, 0.05), TEAM[team], { team });
    flag.position.set(bp.flag[0] - 0.6, 3.1, bp.flag[1]);
    // the shop: a market stall with a team-coloured awning (E/R skills are bought here). Grouped into one
    // object so a raycast against "the stall" (input.ts, gated to the player's own base) hits any part of
    // it, not just the box — a player pointing at the counter or the awning means the same thing.
    const stall = mk(new THREE.BoxGeometry(1.4, 1.0, 1.1), WOOD, { team: 'prop' });
    stall.position.set(bp.shop[0], 0.64, bp.shop[1]);
    const counter = mk(new THREE.BoxGeometry(1.6, 0.12, 1.3), 0xa8845c, { team: 'prop' });
    counter.position.set(bp.shop[0], 1.2, bp.shop[1]);
    const awning = mk(new THREE.ConeGeometry(1.3, 0.8, 4), TEAM[team], { team });
    awning.position.set(bp.shop[0], 2.1, bp.shop[1]);
    awning.rotation.y = Math.PI / 4;
    const posts = [-1, 1].map(k => {
      const post = mk(new THREE.CylinderGeometry(0.05, 0.05, 0.6, 6), WOOD_DARK, { team: 'prop' });
      post.position.set(bp.shop[0] + k * 0.7, 1.55, bp.shop[1]);
      return post;
    });
    const stallHit = new THREE.Group();
    stallHit.add(stall, counter, awning, ...posts);
    // Flat ring under the stall, same idea as a hero's own selection ring: invisible until input.ts says
    // the cursor is over the stall (and the hero is actually home), never driven from here.
    const hoverRing = flat(mk(new THREE.RingGeometry(1.5, 1.78, 32), TEAM[team], { team, cast: false }), 0.15);
    hoverRing.position.set(bp.shop[0], 0.15, bp.shop[1]);
    hoverRing.visible = false;
    root.add(basin, water, spout, pole, flag, stallHit, hoverRing);
    stalls[team] = { hit: stallHit, hoverRing };
  }

  // monster dens: only the rocks — the monsters themselves are dynamic (monsterView.ts)
  L.bosses.forEach(({ pos: [x, z] }) => {
    const rand = spot(x, z, 7);
    for (let k = 0; k < 9; k++) {
      const a = Math.atan2(-x, -z) + Math.PI * 0.35 + k * (Math.PI * 1.3 / 8);
      const r = mk(new THREE.DodecahedronGeometry(0.5 + rand() * 0.3), ROCK, { team: 'prop' });
      r.position.set(x + Math.sin(a) * 4, 0.3, z + Math.cos(a) * 4);
      root.add(r);
    }
  });

  // ink camps: three small hunched blots
  L.camps.forEach(([x, z]) => {
    for (let k = 0; k < 3; k++) {
      const a = k * 2.1, px = x + Math.cos(a) * 0.9, pz = z + Math.sin(a) * 0.9;
      const b = mk(new THREE.SphereGeometry(0.42, 12, 8), 0x6f6a78, { team: 'prop' });
      b.scale.set(1, 0.8, 1.2); b.position.set(px, 0.35, pz);
      const h = mk(new THREE.ConeGeometry(0.1, 0.35, 5), 0xd8cfb8, { team: 'prop' });
      h.position.set(px, 0.8, pz); h.rotation.x = 0.4;
      root.add(b, h);
    }
  });

  // trees and rocks: instanced like the decor — a few draw calls instead of a thousand
  for (const [x, z] of L.trees) {
    const r = spot(x, z), s = 0.85 + r() * 0.45, light = r() < 1 / 3;
    if (treeKind(L, [x, z]) === 'fir') {
      B.put('trunk', () => new THREE.CylinderGeometry(0.22, 0.3, 1.1, 6), 0x6e4b2f, { team: 'prop' }, B.trs(x, 0.55 * s, z, 0, s));
      B.put(light ? 'crownLight' : 'crown', () => new THREE.ConeGeometry(1.5, 3, 7), light ? LEAF : LEAF_DARK, { team: 'foliage' }, B.trs(x, 2.4 * s, z, r() * Math.PI * 2, s));
      continue;
    }
    // an oak: a stout trunk under a round, wide crown of three lumps
    const spin = r() * Math.PI * 2;
    B.put('oakTrunk', () => new THREE.CylinderGeometry(0.26, 0.36, 1.6, 7).translate(0, 0.8, 0), 0x5e4028, { team: 'prop' }, B.trs(x, 0, z, spin, s));
    B.put(light ? 'oakCrownLight' : 'oakCrown', () => new THREE.IcosahedronGeometry(1, 1), light ? 0x5a8f45 : 0x44793a, { team: 'foliage' },
      B.trs(x, 2.5 * s, z, spin, [1.25 * s, 1 * s, 1.25 * s]));
    for (const k of [0, 1]) {
      const a = spin + k * 2.6, off = 0.75 * s;
      B.put('oakLump', () => new THREE.IcosahedronGeometry(1, 1), 0x4f8740, { team: 'foliage' }, B.trs(x + Math.cos(a) * off, (2.1 + k * 0.5) * s, z + Math.sin(a) * off, spin, 0.75 * s));
    }
  }
  for (const [x, z] of L.rocks) {
    const r = spot(x, z);
    B.put('rock', () => new THREE.DodecahedronGeometry(1), ROCK, { team: 'prop' }, B.trs(x, 0.35, z, r() * Math.PI * 2, 0.8 + Math.floor(r() * 3) * 0.2, r() * Math.PI * 2));
  }
  B.flush(root);

  /* Grass is split into map cells, one instanced mesh each, so the camera culls what it can't see. */
  const CELL = 12;
  function scatter(blade: THREE.BufferGeometry, color: number, opts: MkOpts, list: THREE.Matrix4[]) {
    const cells = new Map<string, THREE.Matrix4[]>();
    for (const m of list) {
      const k = Math.floor(m.elements[12]! / CELL) + ',' + Math.floor(m.elements[14]! / CELL);
      if (!cells.has(k)) cells.set(k, []);
      (cells.get(k) as THREE.Matrix4[]).push(m);
    }
    for (const part of cells.values()) {
      const mesh = mk(blade, color, { ...opts, cast: false, count: part.length }) as THREE.InstancedMesh;
      part.forEach((m, n) => mesh.setMatrixAt(n, m));
      mesh.computeBoundingSphere();
      root.add(mesh);
    }
  }
  // blades are open cones: the base is never seen
  const bladeGeo = (r: number, h: number) => new THREE.ConeGeometry(r, h, 3, 1, true).translate(0, h / 2, 0);

  // tall brush: dense dark blades inside each ellipse
  {
    const PER = 912, blades: THREE.Matrix4[] = [];  // thinned 24% from 1200: hiding is the ellipse, not the blades
    const o = new THREE.Object3D();
    for (const b of L.brush) {
      const rand = spot(b[0], b[1], 8);  // each patch of brush from its own spot: editing one leaves the others
      for (let k = 0; k < PER; k++) {
        const a = rand() * Math.PI * 2, d = Math.sqrt(rand());
        const lx = Math.cos(a) * d * b[2], lz = Math.sin(a) * d * b[3];
        const c = Math.cos(b[4]), s = Math.sin(b[4]);
        o.position.set(b[0] + lx * c - lz * s, 0, b[1] + lx * s + lz * c);
        o.rotation.set((rand() - 0.5) * 0.5, rand() * 3, (rand() - 0.5) * 0.5);
        o.scale.set(1, 0.9 + rand() * 0.6, 1);
        o.updateMatrix();
        blades.push(o.matrix.clone());
      }
    }
    scatter(bladeGeo(0.05, 1), 0x5a8a3e, { team: 'prop' }, blades);
  }

  // short grass tufts on open ground (pen strokes), kept off blocked ground, brush and points. Candidates are
  // seeded per 4 m cell, so an edit only thins or fills the grass around what changed.
  {
    const TCELL = 4, PER_CELL = 4, blades: THREE.Matrix4[] = [];
    const o = new THREE.Object3D(), { blocked } = terrain(L);
    for (let i = Math.floor(-L.bounds.x / TCELL); i * TCELL < L.bounds.x; i++) {
      for (let j = Math.floor(-L.bounds.z / TCELL); j * TCELL < L.bounds.z; j++) {
        for (let k = 0; k < PER_CELL; k++) {
          const rand = rng(Math.imul(i, 73856093) ^ Math.imul(j, 19349663) ^ Math.imul(k + 1, 83492791));
          const x = (i + rand()) * TCELL, z = (j + rand()) * TCELL, h = 0.5 + rand() * 0.6, spin = rand() * Math.PI * 2;
          if (blocked(x, z) || L.brush.some(b => inEllipse(x, z, b)) || L.fields.some(f => inRect(x, z, f)) || L.fords.some(f => inRect(x, z, f)) || L.points.some(p => Math.hypot(x - p.pos[0], z - p.pos[1]) < L.pointRadius + 0.5)) continue;
          for (let b = 0; b < 3; b++) {
            const ang = spin + b * 2.1;
            o.position.set(x + Math.cos(ang) * 0.1, 0, z + Math.sin(ang) * 0.1);
            o.rotation.set(Math.sin(ang) * 0.45, rand() * 3, -Math.cos(ang) * 0.45);
            o.scale.set(1, h * (0.7 + rand() * 0.5), 1);
            o.updateMatrix();
            blades.push(o.matrix.clone());
          }
        }
      }
    }
    scatter(bladeGeo(0.022, 0.5), 0x9cc06f, {}, blades);
  }

  return {
    points,
    stalls,
    dispose() {
      scene.remove(root);
      // release() frees geometry and materials; an instanced mesh also owns its instance-matrix buffer
      root.traverse(o => { if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose(); });
      release(root);
      // the arc steps a point isn't showing right now hang off no mesh
      for (const g of [...arcs.a, ...arcs.b]) g.dispose();
    },
  };
}
